/**
 * Owns: cart store attributes, embedded store catalog, store-specific variant pricing.
 * Exposes: loadStoreCatalog, fetchCartAttributes, buildCartAttributes, findStoreFromCartAttributes,
 *   resolveStorePrice, STORE_SELECTED_EVENT, STORE_LOCATOR_SOURCE.
 * Consumes: store-events (event constants), money-formatting.
 */

import { formatMoney } from '@theme/money-formatting';
import { STORE_LOCATOR_SOURCE, STORE_SELECTED_EVENT } from '@theme/store-events';

const CART_ATTR_STORE_LOCATION_ID = 'store_location_id';
const CART_ATTR_SHOPIFY_LOCATION_ID = 'shopify_location_id';
const CART_ATTR_STORE_CODE = 'store_code';
const CART_ATTR_STORE_ZIP = 'store_zip';
const CART_ATTR_DELIVERY_ZIP = 'delivery_zip';
const CART_ATTR_ENABLE_STORE_PRICING = 'enable_store_pricing';

export { CART_ATTR_DELIVERY_ZIP };

/**
 * @typedef {Object} StorePricingVariantData
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {number | null} cmgPrice
 */

/**
 * @typedef {Object} StorePricingConfig
 * @property {string} moneyFormat
 * @property {string} [moneyWithCurrencyFormat]
 * @property {string} currency
 * @property {boolean} useCurrency
 * @property {Record<string, StorePricingVariantData>} variants
 */

/**
 * @typedef {Object} StoreCatalogEntry
 * @property {string} id
 * @property {string} [pageUrl]
 * @property {string} [shopifyLocationId]
 * @property {string} [storeCode]
 * @property {string} [displayName]
 * @property {boolean} [enableStorePricing]
 * @property {Record<string, string[]>} [zipsByRadius]
 * @property {string[]} [hours]
 * @property {{ zipcode?: string }} [address]
 */

/**
 * @param {string} raw
 * @returns {string}
 */
export function normalizeServiceZip(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length < 5) return '';
  return digits.length >= 9 ? digits.slice(-5) : digits.slice(0, 5);
}

/**
 * @param {StoreCatalogEntry | null | undefined} store
 * @param {string} zip
 * @returns {boolean}
 */
export function isZipInStoreServiceArea(store, zip) {
  const normalized = normalizeServiceZip(zip);
  if (!normalized || !store) return false;

  for (const zips of Object.values(store.zipsByRadius ?? {})) {
    if (!Array.isArray(zips)) continue;
    for (const entry of zips) {
      if (normalizeServiceZip(String(entry)) === normalized) return true;
    }
  }

  return false;
}

/**
 * @typedef {Object} ResolvedStorePrice
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {boolean} showStorePriceNote
 */

/**
 * @typedef {Object} ShopifyVariantResource
 * @property {string | number} id
 * @property {number} price
 * @property {number} [compare_at_price]
 */

/**
 * @param {string | undefined | null} id
 * @returns {string}
 */
export function normalizeLocationId(id) {
  if (!id) return '';
  const str = String(id).trim();
  const gidMatch = str.match(/\/(\d+)\s*$/);
  return gidMatch?.[1] ?? str;
}

/**
 * @param {string | undefined | null} left
 * @param {string | undefined | null} right
 * @returns {boolean}
 */
export function storeIdsMatch(left, right) {
  const a = String(left ?? '').trim();
  const b = String(right ?? '').trim();
  if (!a || !b) return false;
  if (a === b) return true;

  const aNorm = normalizeLocationId(a);
  const bNorm = normalizeLocationId(b);
  if (aNorm && bNorm && aNorm === bNorm) return true;

  const aMeta = a.match(/Metaobject\/(\d+)/i)?.[1];
  const bMeta = b.match(/Metaobject\/(\d+)/i)?.[1];
  if (aMeta && bMeta && aMeta === bMeta) return true;
  if (aMeta && aMeta === bNorm) return true;
  if (bMeta && bMeta === aNorm) return true;

  return false;
}

/**
 * @returns {StoreCatalogEntry[]}
 */
export function loadStoreCatalog() {
  const root = document.getElementById('store-locator-root');
  if (!root) return [];

  const chunks = Array.from(root.querySelectorAll('template[data-store-locator-data-chunk]')).filter(
    (element) => element instanceof HTMLTemplateElement
  );

  /** @type {StoreCatalogEntry[]} */
  const stores = [];

  chunks.forEach((chunk, index) => {
    const raw = chunk.content.textContent?.trim();
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        stores.push(...parsed);
      }
    } catch (error) {
      console.warn(`[store-pricing] JSON parse error (chunk ${index})`, error);
    }
  });

  return stores;
}

/**
 * @param {Record<string, string | number | boolean | undefined>} attributes
 * @returns {boolean}
 */
export function hasStoreCartAttributes(attributes) {
  if (!attributes || typeof attributes !== 'object') return false;

  const locationId = String(attributes[CART_ATTR_STORE_LOCATION_ID] ?? '').trim();
  const shopifyLocationId = String(attributes[CART_ATTR_SHOPIFY_LOCATION_ID] ?? '').trim();
  return Boolean(locationId || shopifyLocationId);
}

/**
 * @param {StoreCatalogEntry[]} catalog
 * @param {Record<string, string | number | boolean | undefined>} attributes
 * @returns {StoreCatalogEntry | null}
 */
export function findStoreFromCartAttributes(catalog, attributes) {
  const locationId = String(attributes[CART_ATTR_STORE_LOCATION_ID] ?? '').trim();
  const shopifyLocationId = String(attributes[CART_ATTR_SHOPIFY_LOCATION_ID] ?? '').trim();

  if (locationId) {
    const byMetaobject = catalog.find((store) => storeIdsMatch(store.id, locationId));
    if (byMetaobject) return byMetaobject;
  }

  if (shopifyLocationId) {
    const normalized = normalizeLocationId(shopifyLocationId);
    const byShopify = catalog.find((store) => {
      const storeLocId = store.shopifyLocationId?.trim();
      if (!storeLocId) return false;
      return storeLocId === shopifyLocationId || normalizeLocationId(storeLocId) === normalized;
    });
    if (byShopify) return byShopify;
  }

  const storeCode = String(attributes[CART_ATTR_STORE_CODE] ?? '').trim();
  if (storeCode) {
    return catalog.find((store) => store.storeCode?.trim() === storeCode) ?? null;
  }

  return null;
}

/**
 * @returns {Record<string, string>}
 */
export function buildEmptyStoreCartAttributes() {
  return {
    [CART_ATTR_STORE_LOCATION_ID]: '',
    [CART_ATTR_SHOPIFY_LOCATION_ID]: '',
    [CART_ATTR_STORE_CODE]: '',
    [CART_ATTR_STORE_ZIP]: '',
    [CART_ATTR_DELIVERY_ZIP]: '',
    [CART_ATTR_ENABLE_STORE_PRICING]: '',
  };
}

/**
 * @param {Record<string, string | number | boolean | undefined> | null | undefined} attributes
 * @returns {string}
 */
export function getDeliveryZipFromCartAttributes(attributes) {
  if (!attributes || typeof attributes !== 'object') return '';

  const raw = String(attributes[CART_ATTR_DELIVERY_ZIP] ?? '').trim();
  if (!raw) return '';

  return normalizeServiceZip(raw) || raw;
}

/**
 * @param {string} zip
 * @returns {Record<string, string>}
 */
export function buildDeliveryZipPatch(zip) {
  const raw = String(zip ?? '').trim();
  const normalized = raw ? normalizeServiceZip(raw) || raw : '';

  return {
    [CART_ATTR_DELIVERY_ZIP]: normalized,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} attributes
 * @returns {Record<string, string> | null}
 */
export function normalizeCartAttributes(attributes) {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return null;

  /** @type {Record<string, string>} */
  const normalized = {};

  for (const [key, value] of Object.entries(attributes)) {
    if (value === null || value === undefined) continue;
    const str = String(value).trim();
    if (str) normalized[key] = str;
  }

  return Object.keys(normalized).length > 0 ? normalized : null;
}

/**
 * @param {StoreCatalogEntry} store
 * @returns {Record<string, string>}
 */
export function buildCartAttributes(store) {
  const zipRaw = store.address?.zipcode?.trim() ?? '';
  const storeZip = zipRaw ? normalizeServiceZip(zipRaw) || zipRaw : '';

  return {
    [CART_ATTR_STORE_LOCATION_ID]: String(store.id).trim(),
    [CART_ATTR_SHOPIFY_LOCATION_ID]: store.shopifyLocationId?.trim() ?? '',
    [CART_ATTR_STORE_CODE]: store.storeCode?.trim() ?? '',
    [CART_ATTR_STORE_ZIP]: storeZip,
    [CART_ATTR_ENABLE_STORE_PRICING]: store.enableStorePricing ? 'true' : 'false',
  };
}

/**
 * Resolves a catalog store for the PDP location detail modal, with a display-name fallback.
 * @param {StoreCatalogEntry[]} catalog
 * @param {Record<string, string | number | boolean | undefined>} attributes
 * @param {{ fallbackDisplayName?: string }} [options]
 * @returns {StoreCatalogEntry | null}
 */
export function resolveStoreForLocationDetail(catalog, attributes, options = {}) {
  const matched = findStoreFromCartAttributes(catalog, attributes);
  if (matched) return matched;

  if (!hasStoreCartAttributes(attributes)) return null;

  const fallbackDisplayName = String(options.fallbackDisplayName ?? '').trim();
  if (!fallbackDisplayName) return null;

  return {
    id: String(attributes[CART_ATTR_STORE_LOCATION_ID] ?? ''),
    shopifyLocationId: String(attributes[CART_ATTR_SHOPIFY_LOCATION_ID] ?? ''),
    displayName: fallbackDisplayName,
    hours: [],
    address: {},
  };
}

/**
 * @param {StorePricingVariantData} variant
 * @param {boolean} enableStorePricing
 * @returns {{ price: number; compareAtPrice: number }}
 */
function resolveVariantAmounts(variant, enableStorePricing) {
  let price = variant.price;
  let compareAtPrice = variant.compareAtPrice ?? 0;

  if (enableStorePricing && variant.cmgPrice != null && variant.cmgPrice > 0) {
    price = variant.cmgPrice;
    compareAtPrice = 0;

    if (variant.compareAtPrice > price) {
      compareAtPrice = variant.compareAtPrice;
    } else if (variant.price > price) {
      compareAtPrice = variant.price;
    }
  }

  return { price, compareAtPrice };
}

/**
 * @param {StorePricingVariantData} variant
 * @param {StoreCatalogEntry | null} selectedStore
 * @returns {ResolvedStorePrice}
 */
export function resolveStorePrice(variant, selectedStore) {
  const hasStore = selectedStore != null;
  const enableStorePricing = Boolean(selectedStore?.enableStorePricing);
  const { price, compareAtPrice } = resolveVariantAmounts(variant, enableStorePricing);

  return {
    price,
    compareAtPrice,
    showStorePriceNote: !hasStore,
  };
}

/**
 * @param {StorePricingConfig} config
 * @param {StoreCatalogEntry | null} selectedStore
 * @returns {ResolvedStorePrice}
 */
export function resolveStorePriceMin(config, selectedStore) {
  const variants = Object.values(config.variants ?? {});
  const hasStore = selectedStore != null;

  if (variants.length === 0) {
    return {
      price: 0,
      compareAtPrice: 0,
      showStorePriceNote: !hasStore,
    };
  }

  let minResolved = /** @type {ResolvedStorePrice | null} */ (null);

  for (const variant of variants) {
    const resolved = resolveStorePrice(variant, selectedStore);
    if (minResolved == null || resolved.price < minResolved.price) {
      minResolved = resolved;
    }
  }

  return minResolved ?? { price: 0, compareAtPrice: 0, showStorePriceNote: !hasStore };
}

/**
 * @param {HTMLElement} productPriceEl
 * @returns {StorePricingConfig | null}
 */
export function getStorePricingConfig(productPriceEl) {
  const script = productPriceEl.querySelector('script[data-store-pricing-variants]');
  if (!(script instanceof HTMLScriptElement)) return null;

  try {
    const parsed = JSON.parse(script.textContent ?? '');
    if (!parsed || typeof parsed !== 'object') return null;
    return /** @type {StorePricingConfig} */ (parsed);
  } catch (error) {
    console.warn('[store-pricing] failed to parse variant data', error);
    return null;
  }
}

/** @type {Promise<Record<string, string>> | null} */
let cartAttributesInFlight = null;

/**
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchCartAttributes() {
  if (cartAttributesInFlight) return cartAttributesInFlight;

  cartAttributesInFlight = (async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return {};
      const cart = await response.json();
      const attributes = cart?.attributes;
      if (!attributes || typeof attributes !== 'object') return {};
      return /** @type {Record<string, string>} */ (attributes);
    } catch {
      return {};
    } finally {
      cartAttributesInFlight = null;
    }
  })();

  return cartAttributesInFlight;
}

/**
 * @param {StorePricingConfig} config
 * @returns {string}
 */
function getMoneyFormat(config) {
  if (config.useCurrency && config.moneyWithCurrencyFormat) {
    return config.moneyWithCurrencyFormat;
  }
  return config.moneyFormat || '{{amount}}';
}

/**
 * @param {number} priceCents
 * @param {number} compareAtCents
 * @param {boolean} showSalePriceFirst
 * @param {StorePricingConfig} config
 * @param {boolean} [showDiscountRate]
 * @returns {string}
 */
export function buildPriceMarkup(priceCents, compareAtCents, showSalePriceFirst, config, showDiscountRate = false) {
  const showComparePrice = compareAtCents > priceCents;
  const format = getMoneyFormat(config);
  const currency = config.currency || 'USD';
  const priceFormatted = formatMoney(priceCents, format, currency);
  const compareFormatted = formatMoney(compareAtCents, format, currency);

  const discountRate =
    showDiscountRate && showComparePrice
      ? Math.round(((compareAtCents - priceCents) * 100) / compareAtCents)
      : 0;
  const discountMarkup =
    discountRate > 0 ? `<span class="price-item__discount-rate">-${discountRate}%</span>` : '';

  const regularBlock = `
    <div class="price__regular${showComparePrice ? ' price__hidden' : ''}">
      <span class="price">${priceFormatted || '&nbsp;'}</span>
    </div>`;

  const saleGroups = showSalePriceFirst
    ? `
        <span class="price-item__group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price-item--sale price">${priceFormatted || '&nbsp;'}</span>
        </span>
        <span class="price-item__group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="price-item--regular compare-at-price">${compareFormatted}</span>
        </span>
        ${discountMarkup}`
    : `
        <span class="price-item__group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="price-item--regular compare-at-price">${compareFormatted}</span>
        </span>
        ${discountMarkup}
        <span class="price-item__group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price-item--sale price">${priceFormatted || '&nbsp;'}</span>
        </span>`;

  const saleBlock = `
    <div class="price__sale${showComparePrice ? '' : ' price__hidden'}">
      ${saleGroups}
    </div>`;

  return `${regularBlock}${saleBlock}`;
}

/**
 * @param {HTMLElement} productPriceEl
 * @param {Record<string, string>} cartAttributes
 * @param {StoreCatalogEntry[]} catalog
 */
function updateStorePriceNoteVisibility(productPriceEl, cartAttributes, catalog) {
  const selectedStore = hasStoreCartAttributes(cartAttributes)
    ? findStoreFromCartAttributes(catalog, cartAttributes)
    : null;
  const showStorePriceNote = selectedStore == null;

  productPriceEl.querySelectorAll('[data-store-price-note]').forEach((note) => {
    if (note instanceof HTMLElement) {
      note.hidden = !showStorePriceNote;
    }
  });
}

/**
 * @param {HTMLElement} productPriceEl
 * @param {number} [requestId]
 * @param {string} [expectedVariantId]
 * @returns {boolean}
 */
function isStalePricingRequest(productPriceEl, requestId, expectedVariantId) {
  if (requestId != null && productPriceEl.dataset.pricingRequestId !== String(requestId)) {
    return true;
  }

  if (expectedVariantId) {
    const currentVariantId = productPriceEl.dataset.currentVariantId?.trim();
    if (currentVariantId !== String(expectedVariantId)) {
      return true;
    }
  }

  return false;
}

/**
 * @typedef {Object} ApplyStorePricingOptions
 * @property {number} [requestId]
 * @property {string} [expectedVariantId]
 */

/**
 * @param {ShopifyVariantResource} resource
 * @param {StorePricingConfig | null} config
 * @returns {StorePricingVariantData}
 */
function variantDataFromResource(resource, config) {
  const id = String(resource.id);
  const embedded = config?.variants?.[id];

  return {
    price: resource.price ?? embedded?.price ?? 0,
    compareAtPrice: resource.compare_at_price ?? embedded?.compareAtPrice ?? 0,
    cmgPrice: embedded?.cmgPrice ?? null,
  };
}

function getPriceTarget(productPriceEl, priceContainer) {
  if (productPriceEl.dataset.priceVaries === 'true') {
    const fromTarget = priceContainer.querySelector('.price');
    return fromTarget instanceof HTMLElement ? fromTarget : null;
  }

  const target =
    priceContainer.querySelector('.pdp-price-card__price > div') ??
    priceContainer.querySelector('.price__regular')?.parentElement ??
    priceContainer;

  return target instanceof HTMLElement ? target : null;
}

/**
 * @param {HTMLElement} productPriceEl
 * @param {ResolvedStorePrice} resolved
 * @param {StorePricingConfig} config
 */
function paintResolvedStorePrice(productPriceEl, resolved, config) {
  const priceContainer = productPriceEl.querySelector('[ref="priceContainer"]');
  if (!(priceContainer instanceof HTMLElement)) return;

  const priceTarget = getPriceTarget(productPriceEl, priceContainer);
  if (!priceTarget) return;

  if (productPriceEl.dataset.priceVaries === 'true') {
    const template = productPriceEl.dataset.priceFromTemplate ?? 'From __PRICE__';
    const formatted = formatMoney(resolved.price, getMoneyFormat(config), config.currency);
    priceTarget.textContent = template.replace('__PRICE__', formatted);
  } else {
    const showSalePriceFirst = productPriceEl.dataset.showSalePriceFirst === 'true';
    const showDiscountRate = productPriceEl.dataset.showDiscountRate === 'true';
    priceTarget.innerHTML = buildPriceMarkup(
      resolved.price,
      resolved.compareAtPrice,
      showSalePriceFirst,
      config,
      showDiscountRate
    );
  }

  productPriceEl.querySelectorAll('[data-store-price-note]').forEach((note) => {
    if (note instanceof HTMLElement) {
      note.hidden = !resolved.showStorePriceNote;
    }
  });

  const financingMonths = parseInt(productPriceEl.dataset.financingMonths ?? '', 10);
  if (financingMonths > 0) {
    const financingAmount = Math.ceil(resolved.price / financingMonths / 100) * 100;
    const formattedFinancing = formatMoney(financingAmount, getMoneyFormat(config), config.currency);

    const amountEl = productPriceEl.querySelector('[data-store-financing-amount]');
    if (amountEl instanceof HTMLElement) {
      amountEl.textContent = formattedFinancing;
    }

    const headerEl = productPriceEl.querySelector('[data-store-financing-header]');
    const template = headerEl?.getAttribute('data-financing-header-template');
    if (headerEl instanceof HTMLElement && template) {
      const replacement = `<span class="pdp-price-card__financing-amount" data-store-financing-amount>${formattedFinancing}</span>`;
      headerEl.innerHTML = template.replace('__FINANCING_PRICE__', replacement);
    }
  }
}

/**
 * @param {HTMLElement} productPriceEl
 * @param {ShopifyVariantResource} resource
 * @param {Record<string, string>} cartAttributes
 * @param {StoreCatalogEntry[]} catalog
 * @param {ApplyStorePricingOptions} [options]
 */
export function applyStorePricingFromVariantResource(
  productPriceEl,
  resource,
  cartAttributes,
  catalog,
  options = {}
) {
  const { requestId, expectedVariantId } = options;

  if (isStalePricingRequest(productPriceEl, requestId, expectedVariantId)) {
    return;
  }

  if (resource?.id == null || typeof resource.price !== 'number') {
    return;
  }

  const config = getStorePricingConfig(productPriceEl);
  if (!config) {
    updateStorePriceNoteVisibility(productPriceEl, cartAttributes, catalog);
    return;
  }

  const variantData = variantDataFromResource(resource, config);
  const selectedStore = hasStoreCartAttributes(cartAttributes)
    ? findStoreFromCartAttributes(catalog, cartAttributes)
    : null;
  const resolved = resolveStorePrice(variantData, selectedStore);

  paintResolvedStorePrice(productPriceEl, resolved, config);
}

/**
 * @param {HTMLElement} productPriceEl
 * @param {string} variantId
 * @param {Record<string, string>} cartAttributes
 * @param {StoreCatalogEntry[]} catalog
 * @param {ApplyStorePricingOptions} [options]
 */
export function applyStorePricingToElement(productPriceEl, variantId, cartAttributes, catalog, options = {}) {
  const { requestId, expectedVariantId } = options;

  if (isStalePricingRequest(productPriceEl, requestId, expectedVariantId)) {
    return;
  }

  const config = getStorePricingConfig(productPriceEl);
  if (!config) {
    updateStorePriceNoteVisibility(productPriceEl, cartAttributes, catalog);
    return;
  }

  const variant = config.variants[String(variantId)];
  if (!variant) {
    updateStorePriceNoteVisibility(productPriceEl, cartAttributes, catalog);
    return;
  }

  const selectedStore = hasStoreCartAttributes(cartAttributes)
    ? findStoreFromCartAttributes(catalog, cartAttributes)
    : null;
  const resolved = resolveStorePrice(variant, selectedStore);

  paintResolvedStorePrice(productPriceEl, resolved, config);
}

/**
 * @typedef {Object} RefreshStorePricingOptions
 * @property {number} [requestId]
 * @property {string} [expectedVariantId]
 */

/**
 * @param {HTMLElement} productPriceEl
 * @param {RefreshStorePricingOptions} [options]
 */
export async function refreshStorePricing(productPriceEl, options = {}) {
  if (productPriceEl.dataset.storePricingEnabled !== 'true') return;

  const { requestId, expectedVariantId } = options;
  const priceVaries = productPriceEl.dataset.priceVaries === 'true';

  if (requestId != null) {
    productPriceEl.dataset.pricingRequestId = String(requestId);
  }

  const [cartAttributes, catalog] = await Promise.all([fetchCartAttributes(), Promise.resolve(loadStoreCatalog())]);

  if (priceVaries) {
    if (isStalePricingRequest(productPriceEl, requestId, expectedVariantId)) {
      return;
    }

    const config = getStorePricingConfig(productPriceEl);
    if (!config) {
      updateStorePriceNoteVisibility(productPriceEl, cartAttributes, catalog);
      return;
    }

    const selectedStore = hasStoreCartAttributes(cartAttributes)
      ? findStoreFromCartAttributes(catalog, cartAttributes)
      : null;
    const resolved = resolveStorePriceMin(config, selectedStore);

    paintResolvedStorePrice(productPriceEl, resolved, config);
    return;
  }

  const variantId = expectedVariantId?.trim() ?? productPriceEl.dataset.currentVariantId?.trim();
  if (!variantId) {
    updateStorePriceNoteVisibility(productPriceEl, cartAttributes, catalog);
    return;
  }

  if (isStalePricingRequest(productPriceEl, requestId, expectedVariantId ?? variantId)) {
    return;
  }

  applyStorePricingToElement(productPriceEl, variantId, cartAttributes, catalog, {
    requestId,
    expectedVariantId: expectedVariantId ?? variantId,
  });
}

/**
 * Re-applies CMG store pricing to every enabled product-price in a root (section, grid, document).
 * Section morphing (e.g. collection facet updates) replaces price markup in place without
 * re-running product-price connectedCallback, so callers must invoke this after morph completes.
 *
 * @param {ParentNode} [root=document]
 */
export function refreshStorePricingInRoot(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  const elements = scope.querySelectorAll('product-price[data-store-pricing-enabled="true"]');

  for (const el of elements) {
    if (el instanceof HTMLElement) {
      void refreshStorePricing(el);
    }
  }
}

export { STORE_SELECTED_EVENT, STORE_LOCATOR_SOURCE };
